import {Skeleton} from '@dropbox/dig-components/skeleton';

import styles from './Roadmap.module.css';
import {RoadmapStatusSummarySkeleton} from './RoadmapStatusSummary';

const TABLE_ROWS = 7;
const ROLLUP_TABLE_ROWS = 5;
const SIDEBAR_ROWS: Array<{
  width: number;
  depth: 0 | 1 | 2;
  icon?: boolean;
  separated?: boolean;
  disclosure?: boolean;
}> = [
  {width: 82, depth: 0, icon: true},
  {width: 96, depth: 0, icon: true, separated: true},
  {width: 72, depth: 0, icon: true, separated: true},
  {width: 58, depth: 1, disclosure: true},
  {width: 50, depth: 2},
  {width: 108, depth: 1, disclosure: true},
  {width: 112, depth: 2},
  {width: 124, depth: 2},
  {width: 98, depth: 2},
  {width: 92, depth: 1, disclosure: true},
];

const RoadmapSidebarLoadingSkeleton = () => (
  <aside className={styles.loadingSidebar} aria-label="Loading roadmap sidebar">
    <div className={styles.loadingSidebarNav} aria-hidden="true">
      {SIDEBAR_ROWS.map((row, index) => (
        <div
          key={index}
          className={styles.loadingSidebarRow}
          data-depth={row.depth}
          data-separated={row.separated || undefined}
        >
          {row.disclosure ? (
            <Skeleton.Box height={10} width={10} style={{borderRadius: 2}} />
          ) : row.icon ? (
            <Skeleton.Box height={16} width={16} style={{borderRadius: 999}} />
          ) : (
            <span className={styles.loadingSidebarLeadingSpace} />
          )}
          <Skeleton.Box height={14} style={{width: row.width}} />
        </div>
      ))}
    </div>
  </aside>
);

const TableRows = () => (
  <div className={styles.loadingTableRows}>
    {Array.from({length: TABLE_ROWS}).map((_, index) => (
      <div className={styles.loadingTableRow} key={index}>
        <Skeleton.Box height={16} style={{width: index % 3 === 0 ? '72%' : '58%'}} />
        <Skeleton.Box height={16} style={{width: index % 2 === 0 ? '68%' : '54%'}} />
        <div className={styles.loadingOwner}>
          <Skeleton.Avatar size="small" />
          <Skeleton.Box height={14} width={72} />
        </div>
        <Skeleton.Box height={24} width={88} style={{borderRadius: 999}} />
        <Skeleton.Box height={16} width={76} />
        <Skeleton.Box height={16} width={20} />
      </div>
    ))}
  </div>
);

const TableSkeleton = ({title, count}: {title: string; count?: boolean}) => (
  <section className={styles.loadingSection} aria-label={`Loading ${title.toLowerCase()}`}>
    <div className={styles.loadingSectionHeader}>
      <Skeleton.Box height={24} width={title === 'Recent' ? 64 : 92} />
      {count ? <Skeleton.Box height={18} width={28} style={{borderRadius: 999}} /> : null}
    </div>
    <div className={styles.loadingTable}>
      <div className={styles.loadingTableHeader}>
        <Skeleton.Box height={16} width={72} />
        <Skeleton.Box height={16} width={82} />
        <Skeleton.Box height={16} width={30} />
        <Skeleton.Box height={16} width={46} />
        <Skeleton.Box height={16} width={62} />
        <Skeleton.Box height={16} width={66} />
      </div>
      <TableRows />
    </div>
  </section>
);

const RollupLoadingSkeleton = () => (
  <section className={styles.loadingSection} aria-label="Loading roadmap rollup">
    <div className={styles.loadingRollupSummary} aria-hidden="true">
      <div className={styles.loadingRollupSummaryMetric}>
        <Skeleton.Box height={14} width={82} />
        <Skeleton.Box height={20} width={148} />
      </div>
      <div className={styles.loadingRollupSummaryMetric}>
        <Skeleton.Box height={14} width={72} />
        <Skeleton.Box height={20} width={132} />
      </div>
    </div>
    <div className={styles.loadingTable}>
      <div className={styles.loadingRollupTableHeader}>
        {Array.from({length: 9}).map((_, index) => (
          <Skeleton.Box key={index} height={16} style={{width: index === 0 ? '40%' : '64%'}} />
        ))}
      </div>
      <div className={styles.loadingTableRows}>
        {Array.from({length: ROLLUP_TABLE_ROWS}).map((_, index) => (
          <div className={styles.loadingRollupTableRow} key={index}>
            <Skeleton.Box height={16} style={{width: index % 2 === 0 ? '62%' : '48%'}} />
            {Array.from({length: 8}).map((__, columnIndex) => (
              <Skeleton.Box key={columnIndex} height={16} width={48} />
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DependenciesLoadingSkeleton = () => (
  <section
    className={styles.loadingSection}
    aria-label="Loading roadmap dependencies"
    role="status"
    aria-busy="true"
  >
    <RoadmapStatusSummarySkeleton cardCount={7} progressPlaceholderHeight={108} />
    <div className={styles.loadingDependenciesState} aria-hidden="true">
      <Skeleton.Box height={22} style={{width: '100%'}} />
      <Skeleton.Box height={14} style={{width: '70%'}} />
      <Skeleton.Box height={30} style={{width: '100%'}} />
      <Skeleton.Box height={30} style={{width: '85%'}} />
    </div>
  </section>
);

export const RoadmapContentLoadingSkeleton = ({
  variant = 'default',
}: {
  variant?: 'default' | 'rollup' | 'dependencies';
}) => (
  <div className={styles.loadingContentStack}>
    {variant === 'dependencies' ? (
      <DependenciesLoadingSkeleton />
    ) : variant === 'rollup' ? (
      <RollupLoadingSkeleton />
    ) : (
      <>
        <TableSkeleton title="Recent" />
        <TableSkeleton title="Upcoming" count />
        <TableSkeleton title="Overdue" count />
        <TableSkeleton title="Cancelled" count />
      </>
    )}
  </div>
);

export const RoadmapLoadingSkeleton = ({embedded = false}: {embedded?: boolean}) => (
  <div
    className={embedded ? `${styles.page} ${styles.pageEmbedded}` : styles.page}
    aria-busy="true"
  >
    {embedded ? null : (
      <>
        <RoadmapSidebarLoadingSkeleton />
        <div className={styles.loadingSidebarToggle} aria-hidden="true">
          <Skeleton.Box height={24} width={24} style={{borderRadius: 999}} />
        </div>
      </>
    )}
    <div className={styles.main}>
      <main className={styles.loadingContent} aria-label="Loading roadmap">
        <RoadmapContentLoadingSkeleton />
      </main>
    </div>
  </div>
);
